<template>
  <div class="additional-filters-widget">
    <div class="card h-100">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-funnel"></i>
          Дополнительно
        </h6>
      </div>
      <div class="card-body">
        
        <!-- Уровень подготовки -->
        <div class="filter-section mb-3">
          <label class="form-label small">Уровень подготовки:</label>
          <select class="form-select form-select-sm" v-model="internalAudienceLevel" @change="onAudienceLevelChange">
            <option value="">Все уровни</option>
            <option v-for="level in audienceLevels" :key="level.value" :value="level.value">
              {{ level.label }}
            </option>
          </select>
        </div>

        <!-- Статус модерации -->
        <div class="filter-section mb-3">
          <label class="form-label small">Статус модерации:</label>
          <select class="form-select form-select-sm" v-model="internalModerationStatus" @change="onModerationStatusChange">
            <option value="">Все статусы</option>
            <option v-if="!moderationStatuses || !moderationStatuses.length" disabled>Загрузка...</option>
            <template v-else>
              <option v-for="status in moderationStatuses" :key="status.value" :value="status.value">
                {{ status.label }}
              </option>
            </template>
          </select>
        </div>

        <!-- Фестивали и плейлисты -->
        <div class="filter-section mb-3" v-if="festivals.length > 0">
          <label class="form-label small">Фестиваль/Плейлист:</label>
          <select class="form-select form-select-sm" v-model="internalSelectedFestival" @change="onFestivalChange">
            <option value="">Все мероприятия</option>
            <option v-for="festival in displayedFestivals" :key="festival.id" :value="festival.id">
              {{ festival.caption }}
              <span v-if="festival.materials_count">({{ festival.materials_count }})</span>
            </option>
          </select>
          
          <!-- Поиск по фестивалям если их много -->
          <div v-if="festivals.length > 20" class="festival-search mt-2">
            <input
              type="text"
              class="form-control form-control-sm"
              placeholder="Поиск фестивалей..."
              v-model="festivalSearchQuery"
            />
          </div>
        </div>

        <!-- Быстрые фильтры -->
        <div class="quick-filters mt-3 pt-3 border-top">
          <label class="form-label small">Быстрые фильтры:</label>
          <div class="quick-filter-buttons">
            <button
              class="btn btn-sm quick-filter-btn"
              :class="getQuickFilterClass('moderated')"
              @click="toggleQuickFilter('moderation_status', 'moderated')"
            >
              <i class="bi bi-check-circle"></i>
              Модерированные
            </button>
            <button
              class="btn btn-sm quick-filter-btn"
              :class="getQuickFilterClass('beginner')"
              @click="toggleQuickFilter('audience_level', 'beginner')"
            >
              <i class="bi bi-mortarboard"></i>
              Для начинающих
            </button>
            <button
              class="btn btn-sm quick-filter-btn"
              :class="getQuickFilterClass('free')"
              @click="toggleQuickFilter('is_free', true)"
              v-if="showFreeFilter"
            >
              <i class="bi bi-gift"></i>
              Бесплатные
            </button>
          </div>
        </div>

        <!-- Активные фильтры этого виджета -->
        <div class="active-filters mt-3 pt-3 border-top" v-if="hasActiveFilters">
          <label class="form-label small">Активно:</label>
          <div class="active-filter-badges">
            <span v-if="internalAudienceLevel" class="badge bg-info active-filter-badge">
              {{ getAudienceLevelLabel(internalAudienceLevel) }}
              <button class="btn-close btn-close-white ms-1" @click="clearAudienceLevel"></button>
            </span>
            <span v-if="internalModerationStatus" class="badge bg-success active-filter-badge">
              {{ getModerationStatusLabel(internalModerationStatus) }}
              <button class="btn-close btn-close-white ms-1" @click="clearModerationStatus"></button>
            </span>
            <span v-if="internalSelectedFestival" class="badge bg-warning active-filter-badge">
              Фестиваль
              <button class="btn-close btn-close-white ms-1" @click="clearFestival"></button>
            </span>
          </div>
        </div>

        <!-- Статистика -->
        <div class="stats-summary mt-3 pt-3 border-top">
          <small class="text-muted">
            <div v-if="festivals.length > 0">
              Доступно фестивалей: {{ festivals.length }}
            </div>
            <div v-if="hasActiveFilters">
              Применено фильтров: {{ activeFiltersCount }}
            </div>
          </small>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';

export default {
  name: 'AdditionalFiltersWidget',
  props: {
    audienceLevels: {
      type: Array,
      default: () => []
    },
    moderationStatuses: {
      type: Array,
      default: () => []
    },
    festivals: {
      type: Array,
      default: () => []
    },
    selectedAudienceLevel: {
      type: String,
      default: ''
    },
    selectedModerationStatus: {
      type: String,
      default: ''
    },
    selectedFestival: {
      type: [Number, String],
      default: null
    },
    showFreeFilter: {
      type: Boolean,
      default: true
    }
  },
  emits: [
    'audience-level-selected',
    'moderation-status-selected', 
    'festival-selected'
  ],
  setup(props, { emit }) {
    
    // Локальное состояние
    const internalAudienceLevel = ref(props.selectedAudienceLevel);
    const internalModerationStatus = ref(props.selectedModerationStatus);
    const internalSelectedFestival = ref(props.selectedFestival);
    const festivalSearchQuery = ref('');

    // Computed свойства
    const displayedFestivals = computed(() => {
      if (!props.festivals || !Array.isArray(props.festivals)) return [];
      
      let filtered = props.festivals;
      
      if (festivalSearchQuery.value) {
        const query = festivalSearchQuery.value.toLowerCase();
        filtered = props.festivals.filter(festival =>
          festival.caption && festival.caption.toLowerCase().includes(query)
        );
      }
      
      return filtered.sort((a, b) => (b.materials_count || 0) - (a.materials_count || 0));
    });

    const hasActiveFilters = computed(() => {
      return internalAudienceLevel.value !== '' ||
             internalModerationStatus.value !== '' ||
             internalSelectedFestival.value !== null;
    });

    const activeFiltersCount = computed(() => {
      let count = 0;
      if (internalAudienceLevel.value) count++;
      if (internalModerationStatus.value) count++;
      if (internalSelectedFestival.value) count++;
      return count;
    });

    // Методы
    const onAudienceLevelChange = () => {
      emit('audience-level-selected', internalAudienceLevel.value);
    };

    const onModerationStatusChange = () => {
      emit('moderation-status-selected', internalModerationStatus.value);
    };

    const onFestivalChange = () => {
      const value = internalSelectedFestival.value ? parseInt(internalSelectedFestival.value) : null;
      emit('festival-selected', value);
    };

    const getAudienceLevelLabel = (value) => {
      const level = props.audienceLevels.find(l => l.value === value);
      return level ? level.label : value;
    };

    const getModerationStatusLabel = (value) => {
      const status = props.moderationStatuses?.find(s => s.value === value);
      return status ? status.label : value;
    };

    const clearAudienceLevel = () => {
      internalAudienceLevel.value = '';
      emit('audience-level-selected', '');
    };

    const clearModerationStatus = () => {
      internalModerationStatus.value = '';
      emit('moderation-status-selected', '');
    };

    const clearFestival = () => {
      internalSelectedFestival.value = null;
      emit('festival-selected', null);
    };

    // Быстрые фильтры
    const getQuickFilterClass = (filterValue) => {
      const isActive = internalModerationStatus.value === filterValue ||
                      internalAudienceLevel.value === filterValue;
      return isActive ? 'btn-primary' : 'btn-outline-secondary';
    };

    const toggleQuickFilter = (filterType, value) => {
      if (filterType === 'moderation_status') {
        if (internalModerationStatus.value === value) {
          internalModerationStatus.value = '';
        } else {
          internalModerationStatus.value = value;
        }
        emit('moderation-status-selected', internalModerationStatus.value);
      } else if (filterType === 'audience_level') {
        if (internalAudienceLevel.value === value) {
          internalAudienceLevel.value = '';
        } else {
          internalAudienceLevel.value = value;
        }
        emit('audience-level-selected', internalAudienceLevel.value);
      }
    };

    // Синхронизация с родительскими пропсами
    watch(() => props.selectedAudienceLevel, (newValue) => {
      internalAudienceLevel.value = newValue;
    });

    watch(() => props.selectedModerationStatus, (newValue) => {
      internalModerationStatus.value = newValue;
    });

    watch(() => props.selectedFestival, (newValue) => {
      internalSelectedFestival.value = newValue;
    });

    return {
      internalAudienceLevel,
      internalModerationStatus,
      internalSelectedFestival,
      festivalSearchQuery,
      displayedFestivals,
      hasActiveFilters,
      activeFiltersCount,
      onAudienceLevelChange,
      onModerationStatusChange,
      onFestivalChange,
      getAudienceLevelLabel,
      getModerationStatusLabel,
      clearAudienceLevel,
      clearModerationStatus,
      clearFestival,
      getQuickFilterClass,
      toggleQuickFilter
    };
  }
};
</script>

<style scoped>
.additional-filters-widget .card {
  border: 1px solid var(--yt-border-color, #dee2e6);
}

.card-header {
  background-color: var(--yt-bg-secondary, #f8f9fa);
  border-bottom: 1px solid var(--yt-border-color, #dee2e6);
}

.filter-section {
  margin-bottom: 1rem;
}

.form-select {
  font-size: 0.85rem;
}

.quick-filter-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quick-filter-btn {
  font-size: 0.75rem;
  padding: 6px 10px;
  text-align: left;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.quick-filter-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.quick-filter-btn i {
  margin-right: 6px;
  width: 12px;
}

.active-filter-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.active-filter-badge {
  font-size: 0.7rem;
  padding: 4px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
}

.active-filter-badge .btn-close {
  font-size: 0.6rem;
  opacity: 0.8;
}

.active-filter-badge .btn-close:hover {
  opacity: 1;
}

.stats-summary {
  font-size: 0.8rem;
}

.festival-search .form-control {
  font-size: 0.8rem;
}

@media (max-width: 576px) {
  .quick-filter-buttons {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .quick-filter-btn {
    flex: 1;
    min-width: calc(50% - 3px);
    text-align: center;
    font-size: 0.7rem;
    padding: 8px 6px;
  }

  .active-filter-badges {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 768px) {
  .card-body {
    padding: 12px;
  }

  .filter-section {
    margin-bottom: 0.8rem;
  }
}

/* Dark mode support */
html[data-bs-theme="dark"] .additional-filters-widget .card {
  border-color: var(--yt-border-color, #495057);
}

html[data-bs-theme="dark"] .card-header {
  background-color: var(--yt-bg-secondary, #343a40);
  border-bottom-color: var(--yt-border-color, #495057);
}

html[data-bs-theme="dark"] .quick-filter-btn:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
</style>